import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';

import { AufnahmePunktDocument, istPunktartMitProtokoll, istPunktartMitKurzprotokoll, LastModified, Punktart, dateString } from '../../data-model';
import { DataService } from 'src/app/data.service';
import { LayerService, TransformationService, MapStatus, MapStatusService, StorageService, ConfirmService } from '@luzula/luzula-lib';
import { Command, NavbarService } from 'src/app/navbar.service';
import { AuthService } from 'src/app/auth.service';
import { ValidationService } from '../validation.service';
import { NgbNav } from '@ng-bootstrap/ng-bootstrap';
import { updateAccordingly } from './bohrpunkt-detail.resolver';
import { FilterService } from 'src/app/filter.service';
import { ProjectService } from 'src/app/project.service';

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-tabs',
    templateUrl: './bohrpunkt-tabs.component.html',
    styleUrls: ['./bohrpunkt-tabs.component.css'],
    
})
export class BohrpunktTabsComponent implements OnInit, OnDestroy, AfterViewInit {
	bp!: AufnahmePunktDocument;
	bps: AufnahmePunktDocument[] = [];
	pointsFilter: boolean[] = [];
	
	displayMap = false;
	displayPlausi = false;
	mapStatus!: MapStatus;
	activeId = 'Allgemein';
	dirty = false;
	private dirtySubscription?: Subscription;
	private authChangedSubscription?: Subscription;
	private filtersSubscription?: Subscription;
	private filtersUpdatedSubscription?: Subscription;
	private hasSubscription?: Subscription;
	private routeSubscription?: Subscription;

	@ViewChild('nav', { static: true }) nav!: NgbNav;
	@ViewChild('controlToolbar') controlToolbar?: ElementRef<HTMLDivElement>;
	tabIds: string[] = [];

	istPunktartMitProtokoll = istPunktartMitProtokoll;
	istPunktartMitKurzprotokoll = istPunktartMitKurzprotokoll;
	dateString = dateString;

	C = Command;

	// Toolbar-Position (verschiebbar)
	toolbarPosition = { x: -1, y: 8 }; // -1 = noch nicht initialisiert
	private isDragging = false;
	private dragOffset = { x: 0, y: 0 };

	constructor(
		private cd: ChangeDetectorRef,
		public aus: AuthService,
		private fs: FilterService,
		public ps: ProjectService,
		public ts: TransformationService,
		private ds: DataService,
		private route: ActivatedRoute,
		private router: Router,
		private storage: StorageService,
		public ls: LayerService,
		private mapStatusService: MapStatusService,
		public ns: NavbarService,
		public vs: ValidationService,
		private confirmService: ConfirmService
	) {
		this.dirtySubscription = this.vs.dirty.subscribe((d: boolean) => {
			this.dirty = d;
			this.initButtons();
		});
		this.authChangedSubscription = this.aus.authChanged.subscribe(() => this.initButtons());
		this.hasSubscription = this.vs.has.subscribe((has: boolean) => {
			this.displayPlausi = has;
		});

		this.filtersSubscription = this.fs.apply.subscribe(() => this.onApplyFilter());
		this.filtersUpdatedSubscription = this.fs.filtersUpdated.subscribe(() => this.onApplyFilter());

		this.initButtons();
	}
	
	private initButtons() {
		this.ns.subscribe(Command.print, () => this.onPrint());
		this.ns.subscribe(Command.goBack, () => this.onDiscard());
		// savePoint nur kontrollieren wenn keine HL-Area aktiv ist
		// (HL-Area ist aktiv wenn goBackToPoint aktiv ist)
		if (!this.ns.isActive(Command.goBackToPoint)) {
			const canWrite = this.ps.canI('write', 'points') && this.isAssignedToActiveProject();
			if (canWrite) {
				if (this.dirty) this.ns.subscribe(Command.savePoint, () => this.onSave());
				else this.ns.unsubscribe(Command.savePoint);
			}
			else {
				this.ns.unsubscribe(Command.savePoint);
			}
		}
	}

	/**
	 * Prüft ob der Punkt dem aktiven Projekt zugewiesen ist.
	 * Ohne geöffnetes Projekt ist keine Bearbeitung möglich.
	 */
	isAssignedToActiveProject(): boolean {
		const projectId = this.ps.project();
		if (!projectId) return false; // Kein Projekt aktiv → nicht bearbeitbar

		if (!this.bp?.header?.projects) return false; // Kein projects-Array → nicht zugewiesen

		return this.bp.header.projects.some(p => p.id === projectId);
	}

	/**
	 * Zeigt Warnung wenn Punkt nicht dem aktiven Projekt zugewiesen ist.
	 */
	get showNotAssignedWarning(): boolean {
		const projectId = this.ps.project();
		if (!projectId) return false; // Kein Projekt aktiv → keine Warnung
		return !this.isAssignedToActiveProject();
	}

	/**
	 * Prüft ob der Punkt dem Projekt zugeordnet werden kann.
	 * Nur wenn: Projekt hat Bearbeiter UND User darf Punkte zuordnen.
	 */
	get canAssignToProject(): boolean {
		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) return false;

		// Projekt muss Bearbeiter haben
		const hasBearbeiter = projectDoc.bearbeiter && projectDoc.bearbeiter.length > 0;
		if (!hasBearbeiter) return false;

		// User muss Punkte zuordnen dürfen
		return this.ps.canAssignToProject();
	}

	/**
	 * Ordnet den Punkt dem aktiven Projekt zu.
	 */
	async assignToProject(): Promise<void> {
		const projectDoc = this.ps.currentProjectDoc();
		if (!projectDoc) return;

		// Initialisiere projects-Array falls nicht vorhanden
		if (!this.bp.header.projects) {
			this.bp.header.projects = [];
		}

		// Prüfe ob bereits zugeordnet
		if (this.bp.header.projects.some(p => p.id === projectDoc._id)) return;

		// Füge Zuordnung hinzu
		this.bp.header.projects.push({
			id: projectDoc._id,
			name: projectDoc.name
		});

		// Speichere Punkt
		await this.ds.saveDocument(this.bp);
		this.initButtons();
	}

	/**
	 * Entfernt die Zuordnung des Punktes zum aktiven Projekt.
	 */
	async removeFromProject(): Promise<void> {
		const projectId = this.ps.project();
		if (!projectId) return;

		if (!this.bp.header.projects) return;

		// Entferne Zuordnung
		this.bp.header.projects = this.bp.header.projects.filter(p => p.id !== projectId);

		// Speichere Punkt
		await this.ds.saveDocument(this.bp);
		this.initButtons();
	}

	ngAfterViewInit(): void {
		this.displayPlausi = this.vs.hasAny();
		this.cd.detectChanges(); // Ensure the view is updated after the async data is loaded
		this.tabIds = this.nav.items.map(item => item.id);
	}
	
	ngOnInit() {
		// Toolbar-Position wiederherstellen
		this.restoreToolbarPosition();

		const cvt = (id: string, type: Punktart) => {
			// Punktarten mit Protokoll: Kurzprotokoll -> Allgemein
			if (id === 'Kurzprotokoll' && istPunktartMitProtokoll(type)) return 'Allgemein';
			// Punktarten ohne Protokoll aber mit Kurzprotokoll: Allgemein -> Kurzprotokoll
			if (id === 'Allgemein' && istPunktartMitKurzprotokoll(type)) return 'Kurzprotokoll';
			// Boden/Vegetation nur bei Protokoll-Punkten
			if ((id === 'Boden' || id === 'Vegetation') && istPunktartMitKurzprotokoll(type)) return 'Kurzprotokoll';
			// Fallback: Standard-Tab für den Punkttyp
			if (!id || id === '') {
				return istPunktartMitProtokoll(type) ? 'Allgemein' : 'Kurzprotokoll';
			}
			return id;
		};
		this.routeSubscription = this.route.parent?.data.subscribe(data => {
			this.bp = data['bp'];
			// bps werden lazy geladen um die Navigation nicht zu blockieren
			this.initPoints();
			this.initButtons();
			this.mapStatus = {
				center: this.ts.from(this.bp.header.koordinaten),
				zoom: 15,
				bounds: this.mapStatusService.mapLimits.maxSubBounds
			}
			this.storage.get('BohrpunktActiveTab').then((value: any) => {
				// Konvertiere immer - auch wenn kein gespeicherter Tab vorhanden ist
				// (z.B. Allgemein -> Kurzprotokoll bei Kurzprotokoll-Punkten)
				const newActiveTab = cvt(value || this.activeId, this.bp.type);
				this.activeId = newActiveTab;
				if (this.activeId === 'Karte')
					this.onDisplayMap();
			});
		});
	}

	private async initPoints() {
		const response = await this.ds.queryIndex();
		if (!response?.rows) {
			this.bps = [];
			return;
		}
		this.bps = response.rows.map((row: any) => {
			const mesoklima = row.key[7];
			const stfgData = row.key[8];
			return this.ds.updateBohrpunkt({
				_id: row.id,
				type: row.key[1],
				header: row.key[4],
				state: row.key[5],
				version: row.key[6],
				extendedHeader: (mesoklima || stfgData) ? { mesoklima: mesoklima || undefined, stfg: stfgData ? { naehrkraftStufe: stfgData.nk, stammFeuchteStufe: { first: stfgData.sf1, second: stfgData.sf } } : undefined } : undefined
			}, false); // Kein WMS-Fetch für Listen-Punkte
		});
		this.onApplyFilter();
	}
	
	ngOnDestroy(): void {
		this.ns.unsubscribe(Command.print);
		this.ns.unsubscribe(Command.savePoint);
		this.ns.unsubscribe(Command.discardPoint);
		this.ns.unsubscribe(Command.goBack);
		this.dirtySubscription?.unsubscribe();
		this.authChangedSubscription?.unsubscribe();
		this.filtersSubscription?.unsubscribe();
		this.filtersUpdatedSubscription?.unsubscribe();
		this.hasSubscription?.unsubscribe();
		this.routeSubscription?.unsubscribe();
	}

	onApplyFilter() {
			this.pointsFilter = this.bps.map(b => this.fs.filteredDocIds.has(b._id));
	}
	
	onDisplayMap() {
		this.displayMap = true;
	}

	async onSave() {
		if (!this.dirty) return;
		const confirmed = await this.confirmService.confirm({
			title: 'Änderungen speichern',
			message: 'Sollen die Änderungen an diesem Punkt gespeichert werden?',
			confirmText: 'Speichern',
			confirmClass: 'btn-success'
		});
		if (confirmed) {
			this.ds.saveDocument(this.bp, { user: this.aus.currentUser });
			this.bps = updateAccordingly(this.bp, this.bps);
			this.dirty = false;
			this.vs.dirty.next(false);
		}
	}

	async onDiscard() {
		if (!this.dirty) {
			this.router.navigate(['/']);
			return;
		}
		const result = await this.confirmService.confirm({
			title: 'Ungespeicherte Änderungen',
			message: 'Es gibt ungespeicherte Änderungen an diesem Punkt.',
			confirmText: 'Verwerfen',
			confirmClass: 'btn-warning',
			extraText: 'Speichern & zurück',
			extraClass: 'btn-success',
			cancelText: 'Bleiben'
		});
		if (result === 'extra') {
			await this.onSave();
			this.router.navigate(['/']);
		} else if (result === true) {
			// Dokument aus DB neu laden, um In-Memory-Änderungen zurückzusetzen
			try {
				const freshBp = await firstValueFrom(this.ds.getDocument$(this.bp._id));
				Object.assign(this.bp, this.ds.updateBohrpunkt(freshBp as any, false));
			} catch (e) { /* Fallback: trotzdem navigieren */ }
			this.dirty = false;
			this.vs.dirty.next(false);
			this.router.navigate(['/']);
		}
	}
	
	onPrint() {
		this.router.navigate([`/bohrpunkte/Drucken/${this.bp._id}`]);
	}
	
	onNavChange(data: any) {
		this.storage.setSync('BohrpunktActiveTab', data.nextId);
	}

	onPointDeleted() {
		this.router.navigate(['/']);
	}

	formatLastModified(lm: LastModified): string {
		const date = new Date(lm.time);
		const dateStr = date.toLocaleDateString('de-DE');
		const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
		return `Zuletzt geändert: ${dateStr} ${timeStr} von ${lm.user}`;
	}

	// === Toolbar Drag & Drop ===

	onToolbarDragStart(event: MouseEvent | TouchEvent): void {
		event.preventDefault();
		this.isDragging = true;

		const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
		const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

		// Initialisiere Position beim ersten Drag
		if (this.toolbarPosition.x < 0 && this.controlToolbar) {
			const rect = this.controlToolbar.nativeElement.getBoundingClientRect();
			const parentRect = this.controlToolbar.nativeElement.parentElement?.getBoundingClientRect();
			if (parentRect) {
				this.toolbarPosition.x = rect.left - parentRect.left;
				this.toolbarPosition.y = rect.top - parentRect.top;
			}
		}

		this.dragOffset = {
			x: clientX - this.toolbarPosition.x,
			y: clientY - this.toolbarPosition.y
		};
	}

	@HostListener('document:mousemove', ['$event'])
	@HostListener('document:touchmove', ['$event'])
	onToolbarDragMove(event: MouseEvent | TouchEvent): void {
		if (!this.isDragging) return;

		const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
		const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;

		this.toolbarPosition = {
			x: clientX - this.dragOffset.x,
			y: clientY - this.dragOffset.y
		};
	}

	@HostListener('document:mouseup')
	@HostListener('document:touchend')
	onToolbarDragEnd(): void {
		if (this.isDragging) {
			this.isDragging = false;
			// Position speichern
			this.storage.setSync('controlToolbarPosition', JSON.stringify(this.toolbarPosition));
		}
	}

	private restoreToolbarPosition(): void {
		const saved = this.storage.getSync('controlToolbarPosition');
		if (saved) {
			try {
				const pos = JSON.parse(saved);
				if (pos.x >= 0 && pos.y >= 0) {
					this.toolbarPosition = pos;
				}
			} catch (e) {
				// Ignorieren
			}
		}
	}
}
